import { useTheme } from '@emotion/react';
import { Add, Delete, Edit } from '@mui/icons-material';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Grid,
    InputAdornment,
    IconButton,
    OutlinedInput,
    Stack,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Context } from '../../../App';
import { discountProgramApi, saveDiscountProgramApi } from '../../../redux/Account/Pricing/action';
import SuccessMessageModal from '../../Common/SuccessMessage';
import ProgramModal from './ProgramModal';
import { coreSettingsKeys } from '../../../services/core/settings';

const DISCOUNT_TABLE_STYLES = {
    width: '100%',
    p: '12px',
    border: '1px solid rgba(0, 0, 0, 0.10)',
    borderRadius: '8px',
    boxShadow: '0 2px 3px 0 rgba(0, 0, 0, 0.06)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'center',
    gap: '8px',
    backgroundColor: '#FFF',
};

const TABLE_GRID_STYLES = {
    display: 'grid',
    gridTemplateColumns: `
      1.35fr 12px
      1.35fr 20px
      0.8fr
    `,
    alignItems: 'center',
    width: '100%',
    '@media (max-width: 900px)': {
        gridTemplateColumns: '1fr',
        rowGap: '10px',
    },
};

const TABLE_HEADER_STYLES = {
    ...TABLE_GRID_STYLES,
    width: '100%',
    minHeight: '38px',
    backgroundColor: '#F8F8F8',
    px: 2,
    fontSize: '14px',
    fontWeight: 600,
    color: '#1F1F1F',
    '@media (max-width: 900px)': {
        display: 'none',
    },
};

const TABLE_ROW_STYLES = {
    ...TABLE_GRID_STYLES,
    width: '100%',
    minHeight: '60px',
    px: 2,
    py: 1.5,
};

const INPUT_CELL_STYLES = {
    '& .MuiOutlinedInput-root': {
        height: '40px',
        borderRadius: '8px',
        boxShadow: '0 2px 3px 0 rgba(0, 0, 0, 0.06)',
    },
    '& .MuiOutlinedInput-input': {
        fontSize: '14px',
        color: '#1F1F1F',
        padding: '10px 12px',
    },
};

const DiscountProgram = () => {
    const queryClient = useQueryClient();
    const dispatch = useDispatch();
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
    const { isOpenSideBar } = useContext(Context);

    const [isProgramModal, setIsProgramModal] = useState(false);
    const [updateIndex, setUpdateIndex] = useState(-1);
    const [openSuccessModal, setOpenSuccessModal] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [showLoader, setShowLoader] = useState(false);

    const [discountProgram, setDiscountProgram] = useState([]);
    const [storeData, setStoreData] = useState([]);
    const [deletedMemberships, setDeletedMemberships] = useState([]);
    console.log('🚀 ~ DiscountProgram ~ deletedMemberships:', deletedMemberships);
    const [isDeleteModal, setIsDeleteModal] = useState(false);

    const discountProgramData = useSelector((state) => state?.account.showDiscountProgramData);
    console.log('🚀 ~ DiscountProgram ~ discountProgramData:', discountProgramData);

    const fetchDiscountProgram = async () => {
        setShowLoader(true);
        await dispatch(discountProgramApi({ pagination: false }));
        setShowLoader(false);
    };

    const handleSubmit = async (deleteArray, discountProgramArray) => {
        setShowLoader(true);
        const payload = {
            deletedMemberships: deleteArray && deleteArray.length > 0 ? deleteArray : deletedMemberships,
            memberships: discountProgramArray && discountProgramArray.length > 0 ? discountProgramArray : discountProgram,
        };
        const response = await dispatch(saveDiscountProgramApi(payload));
        if (response) {
            queryClient.invalidateQueries({ queryKey: coreSettingsKeys.discountProgram({ pagination: false }), refetchType: 'all' });
            setSuccessMessage('Changes Saved');
            setOpenSuccessModal(true);
            setDeletedMemberships([]);
            fetchDiscountProgram();
        }
        setIsDeleteModal(false);
        setShowLoader(false);
    };

    useEffect(() => {
        fetchDiscountProgram();
    }, []);

    useEffect(() => {
        if (discountProgramData?.data?.length) {
            const formattedPrograms = discountProgramData.data.map(({ _id, programName, discount }) => ({ _id, programName, discount }));
            setDiscountProgram(formattedPrograms);
            setStoreData(formattedPrograms);
        } else {
            setDiscountProgram([]);
            setStoreData([]);
        }
    }, [discountProgramData]);

    const handleDeleteProgram = (program, index) => {
        // setDiscountProgram((prev) => prev.filter((_, i) => i !== index));
        if (program._id) {
            setDeletedMemberships((prev) => [...prev, program._id]);
            setIsDeleteModal(true);
        }
    };

    return (
        <>
            <Box>
                {/* Main container */}
                <Grid
                    container
                    sx={{ boxShadow: 3, bgcolor: 'white' }}
                    justifyContent="space-between"
                    padding="24px"
                    borderTop="none"
                    borderRadius="10px"
                    marginTop="32px"
                >
                    {/* Header section */}
                    <Grid item xs={12} sm={4} md={4} lg={3} xl={3}>
                        <Typography variant="h5" sx={{ fontWeight: 400, fontSize: '24px' }}>
                            Discount Program
                        </Typography>
                    </Grid>

                    {/* Content section */}
                    <Grid item xs={12} sm={8} md={8} xl={8} direction="row" spacing={2}>
                        <Box sx={DISCOUNT_TABLE_STYLES}>
                            <Box sx={TABLE_HEADER_STYLES}>
                                <Box>Program Name</Box>
                                <Box />
                                <Box>Percent</Box>
                                <Box />
                                <Box sx={{ justifySelf: 'end' }}>Actions</Box>
                            </Box>

                            {Array.isArray(discountProgram) &&
                                discountProgram?.map((program, index) => (
                                    <Box sx={TABLE_ROW_STYLES} key={program?._id || index}>
                                        <Typography
                                            variant="body1"
                                            className="label-500"
                                            sx={{
                                                fontSize: '16px',
                                                color: '#1F1F1F',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {program.programName}
                                        </Typography>

                                        <Box />

                                        <Box sx={{ ...INPUT_CELL_STYLES, width: '100%', maxWidth: '100px' }}>
                                            <OutlinedInput
                                                value={(+program.discount)?.toFixed(2)}
                                                type="number"
                                                disabled
                                                endAdornment={<InputAdornment position="end">%</InputAdornment>}
                                                id={`discount-program-percent-${index}`}
                                                variant="outlined"
                                                fullWidth
                                            />
                                        </Box>

                                        <Box />

                                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end" sx={{ justifySelf: 'end' }}>
                                            <IconButton
                                                size="small"
                                                aria-label="Edit discount program"
                                                onClick={() => {
                                                    setUpdateIndex(index);
                                                    setIsProgramModal(true);
                                                }}
                                                sx={{ p: '4px', width: 28, height: 28 }}
                                            >
                                                <Edit sx={{ fontSize: 20, color: '#646466' }} />
                                            </IconButton>

                                            <IconButton
                                                size="small"
                                                aria-label="Delete discount program"
                                                onClick={() => handleDeleteProgram(program, index)}
                                                sx={{ p: '4px', width: 28, height: 28 }}
                                            >
                                                <Delete sx={{ fontSize: 20, color: '#D32F2F' }} />
                                            </IconButton>
                                        </Stack>
                                    </Box>
                                ))}
                        </Box>

                        <Button
                            variant="outlined"
                            startIcon={<Add />}
                            onClick={() => setIsProgramModal(true)}
                            sx={{
                                width: '100px',
                                height: '38px',
                                padding: '6px 12px',
                                marginTop: '13px',
                                borderRadius: '6px',
                                border: '1px solid #006CDD',
                                background: '#FFF',
                                color: '#006CDD',
                                fontFamily: 'Roboto',
                                fontSize: '15px',
                                fontWeight: 500,
                                lineHeight: '26px',
                                textTransform: 'uppercase',
                                '&:hover': {
                                    border: '1px solid #006CDD',
                                    background: '#FFF',
                                },
                            }}
                        >
                            ADD
                        </Button>

                        {/* Add/edit program modal */}
                        <ProgramModal
                            open={isProgramModal}
                            onClose={setIsProgramModal}
                            onSave={setDiscountProgram}
                            updateIndex={updateIndex}
                            setUpdateIndex={setUpdateIndex}
                            discountProgram={discountProgram}
                            handleSubmitProgram={handleSubmit}
                        />
                    </Grid>

                    {/* <Grid
            container
            justifyContent={"end"}
            spacing={2}
            style={{ marginTop: "20px" }}
          >
            <Grid item>
              <Stack spacing={2} direction="row">
                <Button
                  className="btn-text-size-manage"
                  variant="outlined"
                  color="primary"
                  onClick={() => {
                    setDeletedMemberships([]);
                    setDiscountProgram(storeData);
                  }}
                >
                  CANCEL CHANGES
                </Button>
                <Button
                  className="btn-text-size-manage"
                  variant="contained"
                  color="primary"
                  onClick={() => handleSubmit()}
                >
                  SAVE CHANGES
                </Button>
              </Stack>
            </Grid>
          </Grid> */}
                </Grid>
            </Box>

            {/* delete conformation modal  */}

            <Dialog
                open={isDeleteModal}
                onClose={() => {
                    setIsDeleteModal(false);
                    setDeletedMemberships([]);
                }}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">{'Delete Confirmation'}</DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">Are you sure you want to delete this discount program?</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        className="btn-text-size-manage"
                        onClick={() => {
                            setIsDeleteModal(false);
                            setDeletedMemberships([]);
                        }}
                    >
                        No
                    </Button>
                    <Button
                        className="btn-text-size-manage"
                        onClick={() => {
                            handleSubmit(deletedMemberships, undefined);
                        }}
                        autoFocus
                    >
                        Yes
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog fullScreen={false} open={showLoader}>
                <DialogContent>
                    <CircularProgress />
                </DialogContent>
            </Dialog>
            <SuccessMessageModal message={successMessage} open={openSuccessModal} setOpenSuccessModal={setOpenSuccessModal} />
        </>
    );
};

export default DiscountProgram;
